import React, { useEffect, useState, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  StarHalf,
  Trash2,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle2,
  X,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Calendar,
  User,
  Sparkles
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import {
  getAdminReviews,
  deleteAdminReview,
  AdminReviewParams,
  AdminReviewResponse,
  AdminReviewDeleteResponse
} from './adminApis';

export interface AdminReview {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  userId?: string;
  rating: number;
  comment: string;
  practitionerName?: string;
  serviceName?: string;
  location?: string;
  createdAt: string;
}

const renderStars = (rating: number) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.25 && rating % 1 <= 0.75;

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(
        <Star key={i} className="w-4 h-4 fill-[#E6A93F] text-[#E6A93F]" aria-hidden="true" />
      );
    } else if (i === fullStars && hasHalf) {
      stars.push(
        <StarHalf key={i} className="w-4 h-4 fill-[#E6A93F] text-[#E6A93F]" aria-hidden="true" />
      );
    } else {
      stars.push(
        <Star key={i} className="w-4 h-4 text-gray-200 fill-gray-100" aria-hidden="true" />
      );
    }
  }
  return stars;
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const getInitials = (name?: string): string => {
  if (!name) return 'U';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_BG_COLORS = ['#3F6652', '#B9576B', '#57628F', '#C98726', '#2C4A3B'];

const AdminReviews: React.FC = () => {
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRating, setSelectedRating] = useState<string>('');
  const searchInputId = useId();
  const ratingFilterId = useId();

  // Delete modal state
  const [deletingReview, setDeletingReview] = useState<AdminReview | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchReviews = async (page: number = currentPage) => {
    setLoading(true);
    try {
      const params: AdminReviewParams = {
        page,
        limit: 10,
        search: searchTerm || undefined,
        rating: selectedRating ? parseInt(selectedRating, 10) : undefined,
      };

      const res: AdminReviewResponse = await getAdminReviews(params);
      if (res && res.success) {
        setReviews(res.reviews || []);
        setTotalCount(res.count || 0);
        setTotalPages(res.totalPages || 1);
        setCurrentPage(res.currentPage || page);
      } else {
        setReviews([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('Error fetching admin reviews:', err);
      toast.error(err?.response?.data?.message || 'Failed to fetch reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchReviews(1);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, selectedRating]);

  const handleDeleteConfirm = async () => {
    if (!deletingReview) return;
    setIsDeleting(true);

    try {
      const res: AdminReviewDeleteResponse = await deleteAdminReview(deletingReview._id);
      if (res && res.success) {
        toast.success('Review permanently deleted from database & public site');
        setDeletingReview(null);
        // Refresh review list
        fetchReviews(currentPage);
      } else {
        toast.error(res?.message || 'Failed to delete review');
      }
    } catch (err: any) {
      console.error('Error deleting review:', err);
      toast.error(err?.response?.data?.message || 'Unauthorized or failed to delete review');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F0E3] text-[#15302B] font-inter p-4 sm:p-6 lg:p-8">
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto">
        {/* Top Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 bg-white/80 backdrop-blur-md border border-[#15302B]/10 rounded-3xl p-6 shadow-sm">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono tracking-widest text-[#2C4A3B] uppercase font-bold mb-1">
              <ShieldCheck className="w-4 h-4 text-[#E6A93F]" />
              <span>Admin Control Center · Strict Permissions</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-[#15302B]">
              Customer Reviews Management
            </h1>
            <p className="text-sm text-[#3B5750] mt-1">
              View and permanently delete genuine customer feedback. Modifying customer reviews or ratings is strictly prohibited.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="/instructor-profile"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#2C4A3B] text-white text-xs font-semibold hover:bg-[#15302B] transition-colors shadow-sm"
            >
              <span>Go to Full Dashboard →</span>
            </a>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#15302B] text-[#F7F1E5] text-xs font-medium shadow-sm">
              <MessageSquare className="w-3.5 h-3.5 text-[#E6A93F]" />
              <span>Total Reviews: <strong>{totalCount}</strong></span>
            </span>
          </div>
        </div>

        {/* Filters and Search Bar */}
        <div className="bg-white border border-[#15302B]/10 rounded-2xl p-4 mb-6 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative w-full sm:w-80">
            <label htmlFor={searchInputId} className="sr-only">
              Search by customer, comment, practitioner, service
            </label>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3B5750]/60" />
            <input
              id={searchInputId}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by customer, comment, service..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-[#FAF9F5] border border-[#15302B]/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C4A3B] transition-all placeholder:text-[#3B5750]/50"
            />
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <label htmlFor={ratingFilterId} className="flex items-center gap-2 text-sm text-[#3B5750] font-medium shrink-0">
              <Filter className="w-4 h-4 text-[#2C4A3B]" />
              <span>Rating:</span>
            </label>
            <select
              id={ratingFilterId}
              value={selectedRating}
              onChange={(e) => setSelectedRating(e.target.value)}
              className="w-full sm:w-auto px-3.5 py-2 text-sm bg-[#FAF9F5] border border-[#15302B]/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C4A3B] transition-all font-medium text-[#15302B]"
            >
              <option value="">All Ratings (1 - 5 ★)</option>
              <option value="5">5 Stars ★★★★★</option>
              <option value="4">4 Stars ★★★★☆</option>
              <option value="3">3 Stars ★★★☆☆</option>
              <option value="2">2 Stars ★★☆☆☆</option>
              <option value="1">1 Star ★☆☆☆☆</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className="bg-white border border-[#15302B]/10 rounded-2xl p-6 shadow-sm animate-pulse flex items-center justify-between gap-4"
              >
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
                <div className="w-10 h-10 bg-gray-200 rounded-xl" />
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && reviews.length === 0 && (
          <div className="bg-white border border-[#15302B]/10 rounded-3xl p-12 text-center shadow-sm">
            <div className="w-14 h-14 rounded-full bg-[#15302B]/5 flex items-center justify-center mx-auto mb-4 text-[#2C4A3B]">
              <MessageSquare className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-serif font-bold text-[#15302B] mb-1">
              No Reviews Found
            </h3>
            <p className="text-sm text-[#3B5750]/80 max-w-md mx-auto">
              {searchTerm || selectedRating
                ? 'No customer reviews match your active filter criteria. Try clearing search filters.'
                : 'There are currently no reviews submitted in the database.'}
            </p>
          </div>
        )}

        {/* Reviews List */}
        {!loading && reviews.length > 0 && (
          <div className="space-y-4">
            {reviews.map((r, idx) => {
              const avatarColor = AVATAR_BG_COLORS[idx % AVATAR_BG_COLORS.length];

              return (
                <motion.div
                  key={r._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-[#15302B]/10 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row md:items-start justify-between gap-5"
                >
                  {/* Left Column: Customer & Comment */}
                  <div className="flex items-start gap-4 flex-1">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-serif text-base font-bold shrink-0 shadow-sm"
                      style={{ backgroundColor: avatarColor }}
                    >
                      {getInitials(r.name)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1.5">
                        <span className="font-bold text-[#15302B] text-base">
                          {r.name}
                        </span>

                        {r.location && (
                          <span className="text-xs px-2 py-0.5 rounded-md bg-[#FAF9F5] text-[#3B5750] border border-[#15302B]/10">
                            {r.location}
                          </span>
                        )}

                        {(r.serviceName || r.practitionerName) && (
                          <span className="text-xs px-2 py-0.5 rounded-md bg-[#2C4A3B]/10 text-[#2C4A3B] font-medium">
                            {r.serviceName || r.practitionerName}
                          </span>
                        )}
                      </div>

                      {/* Stars */}
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <div className="flex items-center">{renderStars(r.rating)}</div>
                        <span className="text-xs font-mono font-bold text-[#2C4A3B]">
                          {r.rating}.0 / 5.0
                        </span>
                      </div>

                      {/* Comment */}
                      <p className="text-sm leading-relaxed text-[#3B5750] bg-[#FAF9F5] p-3.5 rounded-xl border border-[#15302B]/5">
                        "{r.comment}"
                      </p>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 mt-2.5 text-xs text-[#3B5750]/70">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(r.createdAt)}
                        </span>
                        {r.email && (
                          <span className="truncate">Email: {r.email}</span>
                        )}
                        {r.phone && (
                          <span>Phone: {r.phone}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Delete Action (Strict View & Delete Only) */}
                  <div className="flex items-center justify-end md:self-center shrink-0 pt-3 md:pt-0 border-t md:border-t-0 border-[#15302B]/10">
                    <button
                      type="button"
                      onClick={() => setDeletingReview(r)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer shadow-sm active:scale-95"
                      title="Permanently Delete Review"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                      <span>Delete Review</span>
                    </button>
                  </div>
                </motion.div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <span className="text-xs text-[#3B5750] font-medium">
                  Showing page {currentPage} of {totalPages} ({totalCount} total reviews)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => fetchReviews(currentPage - 1)}
                    className="p-2 rounded-lg bg-white border border-[#15302B]/15 text-[#15302B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#FAF9F5] transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-mono font-bold px-3 py-1 bg-white border border-[#15302B]/15 rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => fetchReviews(currentPage + 1)}
                    className="p-2 rounded-lg bg-white border border-[#15302B]/15 text-[#15302B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#FAF9F5] transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal Popup */}
        <AnimatePresence>
          {deletingReview && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-[#15302B]/10 relative"
              >
                <button
                  type="button"
                  onClick={() => setDeletingReview(null)}
                  className="absolute top-5 right-5 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6" />
                </div>

                <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
                  Permanently Delete Review?
                </h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Are you sure you want to permanently delete the review submitted by{' '}
                  <strong className="text-gray-900">{deletingReview.name}</strong>?
                </p>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-700 mb-6 italic">
                  "{deletingReview.comment}"
                </div>

                <p className="text-xs text-red-600 font-semibold mb-6 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  This review will be permanently erased from the database and public site.
                </p>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={() => setDeletingReview(null)}
                    className="flex-1 py-2.5 px-4 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={handleDeleteConfirm}
                    className="flex-1 py-2.5 px-4 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isDeleting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        <span>Delete Now</span>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminReviews;
